import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { requestPasswordReset } from "../lib/api";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestPasswordReset(email.trim(), window.location.origin);
      setSent(true);
    } catch (err) {
      // Anti-enumeration: the backend always returns 200, so this branch is
      // mostly defensive (network errors).
      toast.error("Couldn't send reset email", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6"
    >
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200/80 shadow-soft p-8">
        <button
          onClick={() => navigate("/login")}
          data-testid="forgot-back-btn"
          className="inline-flex items-center gap-2 font-figtree text-sm text-[#475569] hover:text-[#0F172A] mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </button>

        {!sent ? (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#ECFDF5] text-[#059669]">
              <Mail className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-outfit text-2xl font-bold tracking-tight text-[#0F172A]">
              Reset your password
            </h1>
            <p className="mt-1 font-figtree text-sm text-[#64748B]">
              Enter your account email and we'll send a one-time link to set a
              new password.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="font-figtree text-xs text-[#475569]">
                  Email
                </Label>
                <Input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
                  placeholder="you@clinic.com"
                />
              </div>
              <Button
                data-testid="forgot-submit"
                type="submit"
                disabled={busy || !email}
                className="w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium py-5"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-sent-state">
            <div className="mx-auto inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#ECFDF5] text-[#059669]">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-outfit text-2xl font-bold tracking-tight text-[#0F172A]">
              Check your email
            </h1>
            <p className="mt-2 font-figtree text-sm text-[#475569] leading-relaxed">
              If an account exists for <strong>{email}</strong>, we just sent it
              a link to reset the password. The link is valid for 60 minutes.
            </p>
            <p className="mt-3 font-figtree text-xs text-[#94A3B8]">
              Don't see it? Check your spam folder, or{" "}
              <Link to="/forgot-password" className="text-[#0F172A] underline">
                try again
              </Link>
              .
            </p>
            <Button
              data-testid="forgot-back-to-login"
              onClick={() => navigate("/login")}
              variant="outline"
              className="mt-6 rounded-full border-slate-200"
            >
              Back to sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
